/**
 * AI Context
 *
 * Provides shared AI state across components.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useAISession, UseAISessionReturn } from '../hooks/useAISession';
import { useAIGeneration, UseAIGenerationReturn } from '../hooks/useAIGeneration';
import { StyleTheme } from '../../../ai/schemas/StyleTheme';
import { DEFAULT_THEME, FEATURED_THEMES } from '../../../ai/styles/themeLibrary';

/**
 * AI Context value type
 */
export interface AIContextValue extends UseAISessionReturn, UseAIGenerationReturn {
  selectedTheme: StyleTheme;
  setSelectedTheme: (theme: StyleTheme) => void;
  availableThemes: StyleTheme[];
  selectedScreenIndex: number;
  setSelectedScreenIndex: (index: number) => void;
}

/**
 * Create context with undefined default
 */
const AIContext = createContext<AIContextValue | undefined>(undefined);

/**
 * AI Provider props
 */
interface AIProviderProps {
  children: ReactNode;
  initialSessionId?: string;
}

/**
 * AI Provider component
 */
export function AIProvider({ children, initialSessionId }: AIProviderProps) {
  const session = useAISession(initialSessionId);
  const generation = useAIGeneration();
  const [selectedTheme, setSelectedTheme] = React.useState<StyleTheme>(DEFAULT_THEME);
  const [selectedScreenIndex, setSelectedScreenIndex] = React.useState(0);

  // Sync selected screen with presentation
  React.useEffect(() => {
    if (session.presentation) {
      const maxIndex = session.presentation.screens.length - 1;
      if (selectedScreenIndex > maxIndex) {
        setSelectedScreenIndex(Math.max(0, maxIndex));
      }
    }
  }, [session.presentation, selectedScreenIndex]);

  const value: AIContextValue = {
    ...session,
    ...generation,
    selectedTheme,
    setSelectedTheme,
    availableThemes: FEATURED_THEMES,
    selectedScreenIndex,
    setSelectedScreenIndex,
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
}

/**
 * Hook to use AI context
 */
export function useAI(): AIContextValue {
  const context = useContext(AIContext);
  if (context === undefined) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
}

/**
 * Hook to check if inside AI provider
 */
export function useAIOptional(): AIContextValue | undefined {
  return useContext(AIContext);
}
